import React, { useEffect, useState } from "react"
import * as Icons from "lucide-react"
import emailApi from "../services/emailApi"

function fmtWhen(iso) {
  try {
    return new Date(iso).toLocaleString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return ""
  }
}

export default function EmailReminders({
  onClose,
  accountId,
  flagged = [],
  onOpenMessage,
}) {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [dismissed, setDismissed] = useState(new Set())

  useEffect(() => {
    let cancel = false
    ;(async () => {
      try {
        const today = new Date()
        const end = new Date(today.getTime() + 7 * 86400000)
        const params = {
          account: accountId,
          start: today.toISOString().slice(0, 10),
          end: end.toISOString().slice(0, 10),
        }
        const res = await emailApi.getCalendarEvents(params)
        if (!cancel) setEvents(res?.data?.events || [])
      } catch {
        if (!cancel) setEvents([])
      } finally {
        if (!cancel) setLoading(false)
      }
    })()
    return () => {
      cancel = true
    }
  }, [accountId])

  const dismiss = key =>
    setDismissed(prev => {
      const n = new Set(prev)
      n.add(key)
      return n
    })

  const eventReminders = (events || [])
    .filter(e => e.start && new Date(e.start) >= new Date(Date.now() - 3600000))
    .map(e => ({
      key: `ev-${e.id}`,
      type: "event",
      title: e.title || "(untitled)",
      when: e.start,
      sub: e.location || e.organizer || "Calendar event",
    }))

  const flagReminders = (flagged || []).map(m => ({
    key: `fl-${m.id || m.message_id}`,
    type: "flag",
    title: m.subject || "(no subject)",
    when: m.received_at || m.sent_at,
    sub: `Follow up · ${m.from_display || m.from_name || m.from_address || ""}`,
    msg: m,
  }))

  const all = [...eventReminders, ...flagReminders]
    .filter(r => !dismissed.has(r.key))
    .sort((a, b) => new Date(a.when || 0) - new Date(b.when || 0))

  return (
    <div className="em-search-overlay" onClick={onClose}>
      <div className="em-reminders" onClick={e => e.stopPropagation()}>
        <div className="em-reminders-head">
          <div className="em-reminders-title">
            <Icons.Bell size={18} /> Reminders
            {all.length > 0 && (
              <span className="em-reminders-count">{all.length}</span>
            )}
          </div>
          <button className="em-reminders-x" onClick={onClose}>
            <Icons.X size={18} />
          </button>
        </div>

        <div className="em-reminders-body">
          {loading && (
            <div className="em-reminders-empty">Loading reminders…</div>
          )}
          {!loading && all.length === 0 && (
            <div className="em-reminders-empty">
              <Icons.BellOff size={30} strokeWidth={1} />
              <p>No reminders</p>
              <span>Upcoming meetings and flagged mail show up here.</span>
            </div>
          )}
          {all.map(r => (
            <div key={r.key} className="em-reminder-item">
              <span className="em-reminder-icon">
                {r.type === "event" ? (
                  <Icons.Calendar size={16} color="#0f6cbd" />
                ) : (
                  <Icons.Flag size={16} color="#d13438" />
                )}
              </span>
              <div
                className="em-reminder-text"
                onClick={() => {
                  if (r.msg && onOpenMessage) {
                    onOpenMessage(r.msg)
                    onClose()
                  }
                }}
              >
                <div className="em-reminder-title">{r.title}</div>
                <div className="em-reminder-sub">{r.sub}</div>
                <div className="em-reminder-when">{fmtWhen(r.when)}</div>
              </div>
              <button
                className="em-reminder-dismiss"
                onClick={() => dismiss(r.key)}
              >
                Dismiss
              </button>
            </div>
          ))}
        </div>

        {all.length > 0 && (
          <div className="em-reminders-foot">
            <button className="em-reminders-dismissall" onClick={onClose}>
              Dismiss All
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
